/* eslint-disable statura/no-floating-point-money --
 * Shared shift classification utility consumed by all four AwardEngines.
 * No $-arithmetic in this file (only hour-band classification), but the
 * eslint config flags floating-point in this directory.
 */

/**
 * Phase 1A (Award Engine Integrity 2026-05) — shared shift classifier.
 *
 * Replaces the four duplicate `segmentShift` functions previously inlined
 * in `nurses.ts`, `aged-care.ts`, `hpss.ts`, and `packages/utils/src/schads.ts`.
 *
 * What the classifier produces (in one pass):
 *
 *   1. `segments[]` — 15-minute chunks classified by day-of-week + hour-of-day
 *      in the tenant's timezone. Used for per-segment ordinary-hours
 *      allocation across the days the worker actually worked.
 *
 *   2. `dominantDayType` — the day type that owns the majority of paid hours
 *      (FWO majority-of-shift rule per library article K700647 — Nurses
 *      cl 17.2(a) on-call, Aged Care cl 26 weekend penalties, Nurses cl 26).
 *      The caller uses this when the clause defines weekend / public-holiday
 *      penalty rate by the shift's overall character (Fri 23:00 → Sat 07:00
 *      → whole 8h paid at Saturday rate, not 1h weekday + 7h Saturday).
 *
 *   3. `shiftCharacter` — the per-clause character of the shift as a whole
 *      (DAY / AFTERNOON / NIGHT / PERMANENT_NIGHT). Used when the clause
 *      defines afternoon / night shift by the shift's finish time (Nurses
 *      cl 27.2, Aged Care cl 26.5(c)). SCHADS uses per-segment loading
 *      instead (cl 25.4(c)); for that engine the classifier returns
 *      `shiftCharacter: 'DAY'` and the engine consumes `segments[].hour`
 *      directly.
 *
 *   4. `hoursByDayType` — accumulated hours per day type, for callers that
 *      need the underlying split (care minutes attribution, leave accrual,
 *      reconciliation diff classification).
 *
 * The three `dominantDayRule` modes:
 *
 *   - `STRICT_MAJORITY` — the day type with the most paid hours wins. Tie
 *     broken by the finish-time day to keep behaviour deterministic. This is
 *     the recommended default for Nurses, Aged Care, HPSS per FWO library
 *     interpretation. **Architecture decision D1 (15 May 2026).**
 *
 *   - `FINISH_TIME` — the dominant day type is whichever day the shift
 *     finishes in. Industry-acknowledged but lacks FWO endorsement; available
 *     for EBA-specific tenants that explicitly stipulate this rule. NOT
 *     exposed as a global tenant knob — opt-in via `eba_agreements`.
 *
 *   - `PER_SEGMENT` — there is no "dominant" day type; loadings are applied
 *     per-segment per the segment's own day type. Required by SCHADS to
 *     preserve byte-equal output across the 49 dual-run regression fixtures
 *     (`dual-run.test.ts`). The classifier still returns a populated
 *     `dominantDayType` field (the finish-time day, for shape consistency),
 *     but SCHADS consumers MUST NOT use it for penalty selection.
 *
 * The segmentation algorithm also differs between modes — see
 * `segmentShiftSchadsStyle` vs `segmentShiftTrimmedStyle` below. The SCHADS
 * legacy code over-counted hours on non-15-minute-multiple shifts (a known
 * v1 bug); we preserve it in `PER_SEGMENT` mode because dual-run fixtures
 * happen to use 15-min-multiple shifts and any algorithmic change risks the
 * byte-equal guarantee. Other modes use the correct trimmed algorithm.
 *
 * See:
 *   - `award-engine-integrity-architecture-2026-05-15.md` §1.1
 *   - `architecture-decisions-and-stewart-brown-bundling-2026-05-15.md` D1
 *   - FWO library K700647 (on-call worked example)
 *   - Nurses cl 17.2(a), 26, 27.2 (MA000034)
 *   - Aged Care cl 26 (MA000018)
 *   - SCHADS cl 25.4(c) (MA000100)
 */

export type DayType = 'weekday' | 'saturday' | 'sunday' | 'publicHoliday';

export type ShiftCharacter =
  /** Ordinary daytime; no shift loading applies. */
  | 'DAY'
  /** Whole-shift afternoon character (Nurses cl 27.2(a) etc.). */
  | 'AFTERNOON'
  /** Whole-shift night character (Nurses cl 27.2(b) etc.). */
  | 'NIGHT'
  /** Permanent night-shift worker (Nurses cl 20.2(c) — +30% instead of +15%). */
  | 'PERMANENT_NIGHT';

export type DominantDayRule = 'STRICT_MAJORITY' | 'FINISH_TIME' | 'PER_SEGMENT';

export interface ShiftSegment {
  /** Exact instants of the classifier cell. Required for immutable D1 traces. */
  startTime: Date;
  endTime: Date;
  /**
   * Hours within this 15-minute chunk. For `PER_SEGMENT` mode (SCHADS),
   * always exactly `0.25`. For the trimmed modes, the last segment may
   * be a fraction of 0.25 (when the shift end isn't aligned to 15 min).
   */
  hours: number;
  dayType: DayType;
  /** Hour of day (0-23) in the tenant's timezone at the START of the segment. */
  hour: number;
}

export interface ShiftCharacterRules {
  /**
   * Whole-shift afternoon classification.
   *
   * Triggered when the shift FINISHES strictly after `finishAfterHour`
   * and at-or-before `finishAtOrBeforeHour`. When `commenceAtOrAfterHour`
   * is also set, BOTH conditions must hold (AND semantics).
   *
   * For Nurses MA000034 cl 20.1(a): `{ commenceAtOrAfterHour: 12,
   * finishAfterHour: 18, finishAtOrBeforeHour: 24 }` — afternoon =
   * commences at-or-after 12:00 AND finishes after 18:00. A 10:00-18:30
   * shift fails the commence predicate; not afternoon.
   *
   * Set `undefined` if the engine does not use whole-shift afternoon
   * classification (SCHADS: per-segment).
   *
   * Phase Codex Pass A (2026-05-19): added `commenceAtOrAfterHour` per
   * P0-4 (Nurses cl 20.1 commence+finish AND-trigger). Pre-Phase the
   * finish-only shape over-paid 10:00-18:30 and 17:00-01:00 patterns.
   * Artifact: docs/operations/clause-verifications/2026-05-19-statura-regulatory-source-shift-classification-primary-source-rebuild.md
   */
  afternoon?: {
    /**
     * Lower commencement bound (inclusive). When set, the shift must
     * commence at-or-after this hour AND satisfy the finish predicate.
     * Omit to retain pre-Phase finish-only semantics.
     */
    commenceAtOrAfterHour?: number;
    finishAfterHour: number;
    finishAtOrBeforeHour: number;
  };

  /**
   * Whole-shift night classification.
   *
   * Triggered when the shift FINISHES strictly after `finishAfterHour`
   * and (depending on `finishStrictlyBefore`) either at-or-before or
   * strictly-before `finishAtOrBeforeHour`. When `commenceAtOrAfterHour`
   * is also set, BOTH conditions must hold (AND semantics).
   *
   * For Nurses MA000034 cl 20.1(b): `{ commenceAtOrAfterHour: 18,
   * finishAfterHour: 0, finishAtOrBeforeHour: 7.5, finishStrictlyBefore: true }`
   * — night = commences at-or-after 18:00 AND finishes STRICTLY BEFORE
   * 07:30. A 17:00-01:00 shift fails the commence predicate; not night.
   * A 22:00-07:30 shift exact-7:30-finish fails the strict finish-before
   * predicate; not night (per cl 20.1(b) verbatim "finishing BEFORE 7.30 am").
   *
   * Note: a shift finishing at exactly midnight is afternoon (00:00 is
   * "at-or-before 24:00"), per cl 27.2(a). The night band starts
   * STRICTLY after midnight.
   *
   * Phase Codex Pass A (2026-05-19):
   *   - added `commenceAtOrAfterHour` per P0-4 commence+finish AND-trigger.
   *   - added `finishStrictlyBefore` per Codex P1-A: cl 20.1(b) "finishing
   *     before 7.30 am" is an exclusive bound; pre-Phase `finishAtOrBeforeHour: 7.5`
   *     with inclusive comparison `<= 7.5` over-paid 07:30-finish night shifts.
   */
  night?: {
    /**
     * Lower commencement bound (inclusive). When set, the shift must
     * commence at-or-after this hour AND satisfy the finish predicate.
     */
    commenceAtOrAfterHour?: number;
    finishAfterHour: number;
    finishAtOrBeforeHour: number;
    /**
     * When true, the upper finish bound is EXCLUSIVE (strict `<`). When
     * false/undefined, INCLUSIVE (`<=`, legacy behavior). Nurses cl 20.1(b)
     * "finishing before 7.30 am" requires `true`. Pre-Phase legacy callers
     * default to inclusive.
     */
    finishStrictlyBefore?: boolean;
  };

  /**
   * Phase Codex Pass A (2026-05-19) — HPSS Award MA000027 cl 26.3
   * OR-triggered single-band shift loading.
   *
   * Verbatim cl 26.3 (FWC consolidated MA000027): "Where the ordinary
   * rostered hours of work of a shiftworker finish between 6.00 pm and
   * 8.00 am OR commence between 6.00 pm and 6.00 am, the employee will
   * be paid 115% of the minimum hourly rate." Single 115% permanent /
   * 140% casual band; no afternoon/night split.
   *
   * When set, the shift gets `shiftCharacter: 'AFTERNOON'` when EITHER:
   *   - finish hour is strictly after `finishAfterHour` and at-or-before
   *     `finishAtOrBeforeHour`, OR
   *   - commence hour is at-or-after `commenceAtOrAfterHour` and strictly
   *     before `commenceBeforeHour`.
   *
   * Returns `'AFTERNOON'` because HPSS engines pay the single band via
   * the `eveningShift` band breakdown field (no separate night band).
   * The character name is shape-historic; the dollar effect is correct.
   *
   * `orTrigger` is mutually exclusive with `afternoon` + `night`. If
   * `orTrigger` is set, the classifier uses it; the others are ignored.
   * This is enforced by HPSS award-data which sets only `orTrigger`.
   *
   * For HPSS: `{ commenceAtOrAfterHour: 18, commenceBeforeHour: 6,
   * finishAfterHour: 18, finishAtOrBeforeHour: 8 }`.
   *
   * Pre-Phase the finish-only `{ finishAfterHour: 18, finishAtOrBeforeHour: 24 }`
   * + `{ finishAfterHour: 0, finishAtOrBeforeHour: 8 }` shape misclassified
   * 18:00-08:30 commence-in-window-but-finish-out-of-window shifts as
   * non-shift, under-paying 15% × ordinary × hours.
   *
   * Artifact: docs/operations/clause-verifications/2026-05-19-statura-regulatory-source-shift-classification-primary-source-rebuild.md
   */
  orTrigger?: {
    /** Commencement window lower bound (inclusive). */
    commenceAtOrAfterHour: number;
    /** Commencement window upper bound (exclusive — wraps over midnight). */
    commenceBeforeHour: number;
    /** Finish window lower bound (strict greater-than). */
    finishAfterHour: number;
    /** Finish window upper bound (inclusive). */
    finishAtOrBeforeHour: number;
  };

  /**
   * Per-segment evening/night band for SCHADS (cl 25.4(c)). When set,
   * the caller is responsible for applying loading per segment based on
   * `segments[].hour`. The classifier itself reports
   * `shiftCharacter: 'DAY'` and does not classify whole-shift.
   *
   * - `eveningStartHour: 20` — segment starting at 20:00 or later (and
   *   not in the night band) is evening (cl 25.4(c)(ii)).
   * - `nightStartHour: 0` + `nightEndHourExclusive: 6` — segment
   *   starting in [00:00, 06:00) is night (cl 25.4(c)(i)).
   */
  fallbackPerSegmentBands?: {
    eveningStartHour: number;
    nightStartHour: number;
    nightEndHourExclusive: number;
  };
}

export interface ClassifyShiftOptions {
  startTime: Date;
  endTime: Date;
  /** Unpaid break in minutes. Subtracted proportionally for paid-hours weighting in `hoursByDayType`. */
  breakMinutes: number;
  /** IANA timezone (e.g. 'Australia/Sydney', 'Australia/Hobart'). */
  timezone: string;
  isPublicHoliday: boolean;
  /** When present, overrides the legacy whole-span flag; ISO instants survive snapshots. */
  publicHolidaySegments?: readonly {
    startTime: string;
    endTime: string;
    isPublicHoliday: boolean;
  }[];
  shiftCharacterRules: ShiftCharacterRules;
  dominantDayRule: DominantDayRule;
  /**
   * When true, force `shiftCharacter = 'PERMANENT_NIGHT'` regardless of
   * clock times. Applies to Nurses cl 20.2(c) and the equivalent
   * clause in other awards (verify per engine).
   */
  permanentNightShiftWorker?: boolean;
}

export interface ClassifiedShift {
  segments: ShiftSegment[];
  /**
   * Day type that owns the majority of paid hours. Selection algorithm
   * depends on `dominantDayRule`. For `PER_SEGMENT` mode, this is the
   * finish-time day type (for shape consistency only — caller should
   * NOT use this for penalty selection).
   */
  dominantDayType: DayType;
  /**
   * Per-clause character of the shift as a whole. For SCHADS-style
   * engines using `fallbackPerSegmentBands`, this is always `'DAY'`
   * — the caller applies per-segment loading from `segments[].hour`.
   */
  shiftCharacter: ShiftCharacter;
  /**
   * Hours accumulated per day type (PAID hours, i.e. weighted by
   * `1 - breakMinutes/totalRawHours`). Subset of `segments[]` reduced
   * by day type. Excludes unpaid break time proportionally.
   */
  hoursByDayType: Record<DayType, number>;
}

/* ═══════════════════════════════════════════════════════════════════════
 * Internal helpers
 * ═══════════════════════════════════════════════════════════════════════ */

/**
 * Returns weekday 0-6 (0=Sun, 6=Sat) in `tz`. Uses `en-AU` locale
 * matching the legacy SCHADS helper for byte-equal preservation.
 */
function getDayOfWeekInTz(d: Date, tz: string): number {
  const fmt = new Intl.DateTimeFormat('en-AU', { timeZone: tz, weekday: 'short' });
  const short = fmt.format(d);
  return (
    {
      Sun: 0,
      Mon: 1,
      Tue: 2,
      Wed: 3,
      Thu: 4,
      Fri: 5,
      Sat: 6,
    } as Record<string, number>
  )[short]!;
}

/**
 * Returns hour-of-day (0-23) in `tz`. `Intl.DateTimeFormat` can return
 * "24" for midnight under some locale + 2-digit combinations; we normalise
 * back to `0` for consistency.
 */
function getHourInTz(d: Date, tz: string): number {
  const fmt = new Intl.DateTimeFormat('en-AU', {
    timeZone: tz,
    hour: '2-digit',
    hour12: false,
  });
  const h = parseInt(fmt.format(d), 10);
  return h === 24 ? 0 : h;
}

function dayTypeAt(d: Date, tz: string, isPublicHoliday: boolean): DayType {
  if (isPublicHoliday) return 'publicHoliday';
  const dow = getDayOfWeekInTz(d, tz);
  if (dow === 6) return 'saturday';
  if (dow === 0) return 'sunday';
  return 'weekday';
}

/**
 * SCHADS-style segmentation: every segment is EXACTLY 0.25h regardless of
 * whether the last chunk runs past the shift end. This over-counts hours
 * on non-15-minute-multiple shifts (a v1 bug in the original SCHADS
 * engine), but is preserved in `PER_SEGMENT` mode to keep the 49
 * `dual-run.test.ts` fixtures byte-equal. All fixtures use 15-min-multiple
 * shifts so the bug never manifests in those tests.
 */
function segmentShiftSchadsStyle(
  start: Date,
  end: Date,
  tz: string,
  isPublicHoliday: boolean,
): ShiftSegment[] {
  const segments: ShiftSegment[] = [];
  const chunkMs = 15 * 60 * 1000;
  const totalMs = end.getTime() - start.getTime();
  if (totalMs <= 0) return segments;

  for (let offset = 0; offset < totalMs; offset += chunkMs) {
    const t = new Date(start.getTime() + offset);
    segments.push({
      startTime: t,
      endTime: new Date(Math.min(t.getTime() + chunkMs, end.getTime())),
      hours: 0.25,
      dayType: dayTypeAt(t, tz, isPublicHoliday),
      hour: getHourInTz(t, tz),
    });
  }
  return segments;
}

/**
 * Trimmed segmentation: the last segment is trimmed to the actual shift
 * end-time, so total `segments[].hours` exactly equals
 * `(end - start) / 3,600,000`. Used by STRICT_MAJORITY and FINISH_TIME
 * modes (Nurses / Aged Care / HPSS).
 */
function segmentShiftTrimmedStyle(
  start: Date,
  end: Date,
  tz: string,
  isPublicHoliday: boolean,
): ShiftSegment[] {
  const segments: ShiftSegment[] = [];
  const totalMs = end.getTime() - start.getTime();
  if (totalMs <= 0) return segments;

  const stepMs = 15 * 60 * 1000;
  const steps = Math.ceil(totalMs / stepMs);
  for (let i = 0; i < steps; i++) {
    const segStart = new Date(start.getTime() + i * stepMs);
    const segEndMs = Math.min(start.getTime() + (i + 1) * stepMs, end.getTime());
    const segEnd = new Date(segEndMs);
    const segHours = (segEnd.getTime() - segStart.getTime()) / (1000 * 60 * 60);
    segments.push({
      startTime: segStart,
      endTime: segEnd,
      hours: segHours,
      dayType: dayTypeAt(segStart, tz, isPublicHoliday),
      hour: getHourInTz(segStart, tz),
    });
  }
  return segments;
}

const ZERO_HOURS_BY_DAY_TYPE: Record<DayType, number> = {
  weekday: 0,
  saturday: 0,
  sunday: 0,
  publicHoliday: 0,
};

function accumulateHoursByDayType(
  segments: ShiftSegment[],
  paidHoursWeight: number,
): Record<DayType, number> {
  const result: Record<DayType, number> = { ...ZERO_HOURS_BY_DAY_TYPE };
  for (const seg of segments) {
    result[seg.dayType] += seg.hours * paidHoursWeight;
  }
  return result;
}

function pickDominantDayType(
  hoursByDayType: Record<DayType, number>,
  segments: ShiftSegment[],
  rule: DominantDayRule,
): DayType {
  if (segments.length === 0) return 'weekday';

  const finishDayType = segments[segments.length - 1]!.dayType;

  if (rule === 'FINISH_TIME' || rule === 'PER_SEGMENT') {
    return finishDayType;
  }

  // STRICT_MAJORITY: pick the day type with the most paid hours. Tie
  // broken by the finish-time day type (deterministic).
  let best: DayType = finishDayType;
  let bestHours = hoursByDayType[finishDayType];
  const order: DayType[] = ['publicHoliday', 'sunday', 'saturday', 'weekday'];
  for (const candidate of order) {
    if (hoursByDayType[candidate] > bestHours) {
      best = candidate;
      bestHours = hoursByDayType[candidate];
    }
  }
  return best;
}

/**
 * Compute commencement hour (0-23) plus fractional minutes/hour, in tz.
 * Used to evaluate `commenceAtOrAfterHour` / `commenceBeforeHour` predicates.
 *
 * A worker who commences at 07:30 has commenceHourFraction = 7.5. Predicates
 * use strict / inclusive boundaries per the rule shape.
 */
function getCommenceHourFractional(d: Date, tz: string): number {
  const h = getHourInTz(d, tz);
  const m = getMinuteInTz(d, tz);
  return h + m / 60;
}

function classifyWholeShiftCharacter(
  startTime: Date,
  endTime: Date,
  timezone: string,
  rules: ShiftCharacterRules,
  permanentNightShiftWorker: boolean,
): ShiftCharacter {
  if (permanentNightShiftWorker) return 'PERMANENT_NIGHT';

  // When the engine uses per-segment bands (SCHADS), there is no whole-shift
  // afternoon/night classification — the caller derives loading per
  // segment from `segments[].hour`.
  if (rules.fallbackPerSegmentBands && !rules.afternoon && !rules.night && !rules.orTrigger) {
    return 'DAY';
  }

  // Compute the shift's finish-time hour-of-day. Special case: if the
  // shift ends exactly at midnight (00:00), conventional interpretation
  // is that it "finishes at 24:00" — the prior calendar day's last hour.
  // We detect this by checking whether endTime is at a midnight boundary
  // in `timezone` AND the prior moment is the same calendar date.
  //
  // For the comparison: a shift ending at 22:00 has finishHour=22; a shift
  // ending at 00:00 has finishHour=0 but conceptually finishes at 24:00.
  // We use 24 in the latter case so that the Nurses cl 27.2(a) band
  // `(18, 24]` correctly classifies it as afternoon.
  //
  // Phase Codex Pass A (2026-05-19) — fractional-minute precision is
  // now applied so that boundary predicates like Nurses cl 20.1(b)
  // "finishing before 7.30 am" and 18:30 finish-after-18 can be
  // evaluated exactly. Pre-Phase no test used a fractional finish hour
  // (grepped 74 existing test files) so this generalises cleanly. The
  // exact-midnight 24:00 case is preserved.
  const finishHourInt = getHourInTz(endTime, timezone);
  const finishMinute = getMinuteInTz(endTime, timezone);
  const finishSecond = getSecondInTz(endTime, timezone);
  const isExactMidnight = finishHourInt === 0 && finishMinute === 0 && finishSecond === 0;
  let finishHour: number;
  if (isExactMidnight) {
    finishHour = 24;
  } else {
    finishHour = finishHourInt + finishMinute / 60;
  }

  // Phase Codex Pass A (2026-05-19) — HPSS cl 26.3 OR-trigger evaluated
  // first per rule-shape priority (HPSS files set ONLY `orTrigger`).
  if (rules.orTrigger) {
    const t = rules.orTrigger;
    const commenceHour = getCommenceHourFractional(startTime, timezone);

    // Finish predicate. Wraps midnight when `finishAfterHour > finishAtOrBeforeHour`
    // (HPSS cl 26.3: "finish between 6.00 pm and 8.00 am" — wraps).
    let finishInWindow: boolean;
    if (t.finishAfterHour < t.finishAtOrBeforeHour) {
      // Normal window (e.g. 18-24): strict > AND inclusive <=.
      finishInWindow = finishHour > t.finishAfterHour && finishHour <= t.finishAtOrBeforeHour;
    } else {
      // Wraps midnight (e.g. 18-8): strict > AfterHour OR inclusive <= AtOrBeforeHour.
      finishInWindow = finishHour > t.finishAfterHour || finishHour <= t.finishAtOrBeforeHour;
    }

    // Commence predicate. Wraps midnight when `commenceBeforeHour < commenceAtOrAfterHour`
    // (HPSS cl 26.3: "commence between 6.00 pm and 6.00 am" — wraps).
    let commenceInWindow: boolean;
    if (t.commenceBeforeHour > t.commenceAtOrAfterHour) {
      // Normal window (e.g. 10-13): inclusive >= AND strict <.
      commenceInWindow =
        commenceHour >= t.commenceAtOrAfterHour && commenceHour < t.commenceBeforeHour;
    } else {
      // Wraps midnight (e.g. 18-6): inclusive >= AfterHour OR strict < BeforeHour.
      commenceInWindow =
        commenceHour >= t.commenceAtOrAfterHour || commenceHour < t.commenceBeforeHour;
    }
    return finishInWindow || commenceInWindow ? 'AFTERNOON' : 'DAY';
  }

  // Phase Codex Pass A (2026-05-19) — Nurses cl 20.1 commence+finish
  // AND-trigger. When `commenceAtOrAfterHour` is set, BOTH commence and
  // finish predicates must be true. Pre-Phase finish-only legacy semantics
  // remain available for any rule shape that omits commence
  // (integer-hour semantics retained for backward-compat with existing
  // Nurses + Aged Care tests).
  const commenceHour = getCommenceHourFractional(startTime, timezone);

  if (
    rules.afternoon &&
    finishHour > rules.afternoon.finishAfterHour &&
    finishHour <= rules.afternoon.finishAtOrBeforeHour &&
    (rules.afternoon.commenceAtOrAfterHour == null ||
      commenceHour >= rules.afternoon.commenceAtOrAfterHour)
  ) {
    return 'AFTERNOON';
  }
  if (rules.night) {
    const nightFinishOk = rules.night.finishStrictlyBefore
      ? finishHour > rules.night.finishAfterHour && finishHour < rules.night.finishAtOrBeforeHour
      : finishHour > rules.night.finishAfterHour && finishHour <= rules.night.finishAtOrBeforeHour;
    if (
      nightFinishOk &&
      (rules.night.commenceAtOrAfterHour == null ||
        commenceHour >= rules.night.commenceAtOrAfterHour)
    ) {
      return 'NIGHT';
    }
  }
  return 'DAY';
}

function getMinuteInTz(d: Date, tz: string): number {
  const fmt = new Intl.DateTimeFormat('en-AU', {
    timeZone: tz,
    minute: '2-digit',
    hour12: false,
  });
  return parseInt(fmt.format(d), 10);
}

function getSecondInTz(d: Date, tz: string): number {
  const fmt = new Intl.DateTimeFormat('en-AU', {
    timeZone: tz,
    second: '2-digit',
    hour12: false,
  });
  return parseInt(fmt.format(d), 10);
}

/* ═══════════════════════════════════════════════════════════════════════
 * Public API
 * ═══════════════════════════════════════════════════════════════════════ */

export function classifyShift(opts: ClassifyShiftOptions): ClassifiedShift {
  let segments =
    opts.dominantDayRule === 'PER_SEGMENT'
      ? segmentShiftSchadsStyle(opts.startTime, opts.endTime, opts.timezone, opts.isPublicHoliday)
      : segmentShiftTrimmedStyle(opts.startTime, opts.endTime, opts.timezone, opts.isPublicHoliday);

  if (opts.publicHolidaySegments) {
    const spans = opts.publicHolidaySegments.map((span) => ({
      start: Date.parse(span.startTime),
      end: Date.parse(span.endTime),
      holiday: span.isPublicHoliday,
    }));
    // Reject incomplete calendar evidence instead of silently falling back to a start flag.
    if (
      !spans.length ||
      spans.some(
        (span, i) =>
          !Number.isFinite(span.start) ||
          !Number.isFinite(span.end) ||
          span.end <= span.start ||
          (i > 0 && span.start !== spans[i - 1]!.end),
      ) ||
      spans[0]!.start > opts.startTime.getTime() ||
      spans[spans.length - 1]!.end < opts.endTime.getTime()
    ) {
      throw new Error('Public holiday calendar spans must cover the full shift without gaps.');
    }
    segments = segments.flatMap((cell) =>
      spans.flatMap((span) => {
        const start = Math.max(cell.startTime.getTime(), span.start);
        const end = Math.min(cell.endTime.getTime(), span.end);
        if (end <= start) return [];
        const instant = new Date(start);
        return [
          {
            startTime: instant,
            endTime: new Date(end),
            hours: (end - start) / 3_600_000,
            dayType: dayTypeAt(instant, opts.timezone, span.holiday),
            hour: getHourInTz(instant, opts.timezone),
          },
        ];
      }),
    );
  }

  // Compute paid-hours weight for `hoursByDayType`: total hours minus
  // unpaid break, divided by raw hours. The dominant-day-type decision is
  // based on PAID hours (not raw), since the clause text refers to
  // "the shift" — meaning the worked portion.
  const totalMs = opts.endTime.getTime() - opts.startTime.getTime();
  const rawHours = totalMs / (1000 * 60 * 60);
  const breakHours = opts.breakMinutes / 60;
  const paidHours = Math.max(0, rawHours - breakHours);
  const paidWeight = rawHours > 0 ? paidHours / rawHours : 0;

  const hoursByDayType = accumulateHoursByDayType(segments, paidWeight);
  const dominantDayType = pickDominantDayType(hoursByDayType, segments, opts.dominantDayRule);

  const shiftCharacter = classifyWholeShiftCharacter(
    opts.startTime,
    opts.endTime,
    opts.timezone,
    opts.shiftCharacterRules,
    opts.permanentNightShiftWorker ?? false,
  );

  return { segments, dominantDayType, shiftCharacter, hoursByDayType };
}

/**
 * Returns the day-type breakdown of the LAST `overtimeHours` paid hours of
 * the shift, walking backward from the end. Used by `calculateWithOvertime`
 * in each engine to apply the substitution rule (Nurses cl 19.1(c),
 * SCHADS cl 28) per actual segment day-type instead of assuming end-of-shift
 * day-type — fixing the midnight-spanning OT bug per architecture §1.2.
 *
 * The OT hours are paid hours, so we walk PAID segments (the `hours` field
 * already excludes proportional break time when paidWeight < 1 in the
 * `classifyShift` accumulation — but `segments[].hours` itself does NOT
 * reflect break weighting; only `hoursByDayType` does). For the OT
 * backward-walk we use the segment hours directly since OT applies after
 * the break.
 *
 * `lastSegmentHour` is the hour-of-day (0-23) of the LATEST segment in the
 * shift, used by callers that need to know whether OT extends into the
 * night band.
 */
export type OvertimeShiftLoadingBand = 'day' | 'afternoon' | 'night';

/**
 * Classify a weekday segment's hour into an afternoon/night/day loading band.
 * Used when OT minutes must inherit the band they actually fall in, not the
 * whole-shift character.
 */
export function classifySegmentHourShiftLoadingBand(
  segmentHour: number,
  rules: ShiftCharacterRules,
): OvertimeShiftLoadingBand {
  if (rules.fallbackPerSegmentBands) {
    const { eveningStartHour, nightStartHour, nightEndHourExclusive } =
      rules.fallbackPerSegmentBands;
    if (segmentHour >= nightStartHour && segmentHour < nightEndHourExclusive) {
      return 'night';
    }
    if (segmentHour >= eveningStartHour) return 'afternoon';
    return 'day';
  }

  // Nurses MA000034 cl 20.2 segment-position bands for OT placement.
  if (segmentHour >= 18 || segmentHour < 7.5) return 'night';
  if (segmentHour >= 12 && segmentHour < 18) return 'afternoon';
  return 'day';
}

/**
 * Walk backward from shift end and allocate OT hours to afternoon/night
 * loading bands using classified segment positions.
 */
export function getOvertimeShiftLoadingBandBreakdown(
  classified: ClassifiedShift,
  overtimeHours: number,
  rules: ShiftCharacterRules,
): { eveningShift: number; nightShift: number } {
  const result = { eveningShift: 0, nightShift: 0 };
  if (overtimeHours <= 0 || classified.segments.length === 0) return result;

  let remaining = overtimeHours;
  for (let i = classified.segments.length - 1; i >= 0 && remaining > 0; i--) {
    const seg = classified.segments[i]!;
    const take = Math.min(seg.hours, remaining);
    remaining -= take;
    if (seg.dayType !== 'weekday') continue;
    const band = classifySegmentHourShiftLoadingBand(seg.hour, rules);
    if (band === 'afternoon') result.eveningShift += take;
    else if (band === 'night') result.nightShift += take;
  }
  return result;
}

/**
 * Pick the shift-loading band for weekday OT premium selection from the OT
 * tail segments (end-of-shift OT minutes).
 */
export function pickOvertimeShiftLoadingBand(
  classified: ClassifiedShift,
  overtimeHours: number,
  rules: ShiftCharacterRules,
): 'WEEKDAY_AFTERNOON' | 'WEEKDAY_NIGHT' | null {
  if (overtimeHours <= 0 || classified.segments.length === 0) return null;

  let remaining = overtimeHours;
  for (let i = classified.segments.length - 1; i >= 0 && remaining > 0; i--) {
    const seg = classified.segments[i]!;
    const take = Math.min(seg.hours, remaining);
    remaining -= take;
    if (seg.dayType !== 'weekday') continue;
    const band = classifySegmentHourShiftLoadingBand(seg.hour, rules);
    if (band === 'night') return 'WEEKDAY_NIGHT';
    if (band === 'afternoon') return 'WEEKDAY_AFTERNOON';
  }
  return null;
}

export function getOvertimeSegmentBreakdown(
  classified: ClassifiedShift,
  overtimeHours: number,
): { hoursByDayType: Record<DayType, number>; lastSegmentHour: number } {
  const result: Record<DayType, number> = { ...ZERO_HOURS_BY_DAY_TYPE };
  if (classified.segments.length === 0 || overtimeHours <= 0) {
    return { hoursByDayType: result, lastSegmentHour: -1 };
  }

  const lastSegmentHour = classified.segments[classified.segments.length - 1]!.hour;
  let remaining = overtimeHours;
  for (let i = classified.segments.length - 1; i >= 0 && remaining > 0; i--) {
    const seg = classified.segments[i]!;
    const take = Math.min(seg.hours, remaining);
    result[seg.dayType] += take;
    remaining -= take;
  }
  return { hoursByDayType: result, lastSegmentHour };
}

/**
 * Helper exposed for callers that need to translate a `ShiftCharacter`
 * back to the per-engine band code (e.g. evening/night loading lookups).
 * Currently used by Nurses + Aged Care + HPSS; SCHADS uses per-segment
 * loading and ignores this helper.
 */
export function shiftCharacterToBandCode(
  character: ShiftCharacter,
): 'DAY' | 'AFTERNOON' | 'NIGHT' | 'PERMANENT_NIGHT' {
  return character;
}

/* ═══════════════════════════════════════════════════════════════════════
 * Phase Codex Pass A (2026-05-19) — Aged Care MA000018 cl 26.1
 * commencement-band classification
 * ═══════════════════════════════════════════════════════════════════════ */

/**
 * Aged Care Award MA000018 cl 26.1 commencement-band code.
 *
 * Bands per FWC consolidated MA000018 cl 26.1 (verified via FWO PayCat
 * summary + Tanda MA000018 managed template):
 *
 *   - `AFTERNOON_10`     — afternoon commencing at 10:00am and before 1:00pm  (10%   loading)
 *   - `AFTERNOON_12_5`   — afternoon commencing at 1:00pm  and before 4:00pm  (12.5% loading)
 *   - `NIGHT_15`         — night     commencing at 4:00pm  and before 4:00am  (15%   loading)
 *   - `EARLY_MORNING_10` — early-morning commencing at 4:00am and before 6:00am (10% loading)
 *   - `NONE`             — no shift loading applies
 *
 * Pre-Phase the engine used a finish-time Nurses-shape port that
 * misclassified 1pm-9:30pm (12.5% under-paid as 10%) and 4pm-midnight
 * (15% under-paid via finish-only afternoon classification). FW Act
 * s.327A wage-theft criminal regime exposure.
 *
 * Artifact: docs/operations/clause-verifications/2026-05-19-statura-regulatory-source-shift-classification-primary-source-rebuild.md
 */
export type AgedCareCommencementBand =
  | 'AFTERNOON_10'
  | 'AFTERNOON_12_5'
  | 'NIGHT_15'
  | 'EARLY_MORNING_10'
  | 'NONE';

/**
 * Aged Care commencement-band loading values, hardcoded per cl 26.1.
 *
 * These are LOADING PERCENTAGES (the "+10%" etc. on top of the
 * ordinary base rate), NOT multipliers. Apply as
 * `baseHourlyRate × loading × hours` for the additive shift-loading
 * extra (additive on top of ordinary base, mirroring the existing
 * `(eveningMult - weekdayMult) × base × hours` pattern).
 *
 * Hardcoded here because the DB seed at
 * `supabase/migrations/20260608100000_seed_aged_care_v1.sql:182-184`
 * acknowledges these 4 bands in comments but only seeds 2 multipliers
 * (12.5% and 15%) labelled `WEEKDAY_EVENING` + `WEEKDAY_NIGHT`. Storing
 * the 4-band structure in the engine is the smallest patch that closes
 * the wage defect without requiring a DB reseed (the reseed is a
 * concurrent W-task; the engine reading hardcoded loading values until
 * AWR 2026 22 Jun 2026 reseed window is correct because all four 10%
 * /12.5%/15% values are derived from FWC primary source and have been
 * stable across PR791119 + every prior reseed).
 *
 * AWR 2026 reseed window (22 Jun 2026) MUST verify these loading
 * percentages against the new FWC pay guide and update if any change
 * (none expected — cl 26.1 percentages have been stable since 2010).
 */
export const AGED_CARE_COMMENCEMENT_BAND_LOADINGS: Readonly<
  Record<AgedCareCommencementBand, number>
> = {
  AFTERNOON_10: 0.1,
  AFTERNOON_12_5: 0.125,
  NIGHT_15: 0.15,
  EARLY_MORNING_10: 0.1,
  NONE: 0,
};

/**
 * Classify an Aged Care shift's cl 26.1 commencement band based on the
 * shift START time. Returns `'NONE'` if the shift does not commence in
 * any of the four bands.
 *
 * Per cl 26.1 the band is determined by COMMENCEMENT time only — the
 * finish time is irrelevant. Bands are mutually exclusive (a shift
 * commencing in [10:00, 13:00) cannot also be in [13:00, 16:00) — they
 * partition the day).
 *
 * Caller must ensure the shift falls on a weekday (cl 26.1 does not
 * apply on Saturday / Sunday / public holidays — those are paid as
 * penalty bands per cl 26.2 / 26.4). This classifier does NOT
 * check the day-type; the engine consumes `dominantDayType === 'weekday'`
 * separately before consulting this function.
 *
 * Boundary semantics (specialist-verified primary source):
 *   - "commencing at 10.00 am AND before 1.00 pm" → [10:00, 13:00)
 *   - "commencing at 1.00 pm  AND before 4.00 pm" → [13:00, 16:00)
 *   - "commencing at 4.00 pm  AND before 4.00 am" → [16:00, 24:00) ∪ [00:00, 04:00)
 *   - "commencing at 4.00 am  AND before 6.00 am" → [04:00, 06:00)
 *
 * Worker commencing at exactly 10:00 → AFTERNOON_10 (inclusive lower).
 * Worker commencing at exactly 13:00 → AFTERNOON_12_5 (inclusive lower).
 * Worker commencing at exactly 09:59 → NONE (sub-minimum).
 */
export function classifyAgedCareCommencementBand(
  startTime: Date,
  timezone: string,
): AgedCareCommencementBand {
  const h = getCommenceHourFractional(startTime, timezone);

  // Night 15% — commences at 4:00pm and before 4:00am (wraps midnight).
  if (h >= 16 || h < 4) return 'NIGHT_15';
  // Early-morning 10% — commences at 4:00am and before 6:00am.
  if (h >= 4 && h < 6) return 'EARLY_MORNING_10';
  // Afternoon 12.5% — commences at 1:00pm and before 4:00pm.
  if (h >= 13 && h < 16) return 'AFTERNOON_12_5';
  // Afternoon 10% — commences at 10:00am and before 1:00pm.
  if (h >= 10 && h < 13) return 'AFTERNOON_10';
  // Sub-minimum (06:00 ≤ commence < 10:00) — no shift loading.
  return 'NONE';
}
